import { useCallback, useEffect, useState } from "react";
import { demoLogin, googleLogin, login, signup, type AuthUser } from "../api";
import type { Nav, Route } from "../types";
import { useT } from "../i18n";
import { GoogleSignInButton } from "../components/GoogleSignInButton";

interface Props {
  route: Route & { page: "auth" };
  nav: Nav;
  onLogin: (user: AuthUser) => void;
}

export function AuthPage({ route, nav, onLogin }: Props) {
  const t = useT();
  const [mode, setMode] = useState<"login" | "signup">(route.mode ?? "login");
  useEffect(() => {
    if (route.mode) setMode(route.mode);
  }, [route.mode]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !password) {
      setError(t("auth.errMissingEmail"));
      return;
    }
    if (mode === "signup" && !displayName) {
      setError(t("auth.errMissingName"));
      return;
    }
    setError("");
    setLoading(true);
    try {
      const user =
        mode === "login"
          ? await login(email, password)
          : await signup(email, password, displayName);
      onLogin(user);
      nav({ page: "home" });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  async function asDemo() {
    setError("");
    setLoading(true);
    try {
      const user = await demoLogin();
      onLogin(user);
      nav({ page: "home" });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  const onGoogleCredential = useCallback(
    async (credential: string) => {
      setError("");
      setLoading(true);
      try {
        const user = await googleLogin(credential);
        onLogin(user);
        nav({ page: "home" });
      } catch (err) {
        setError(
          t("auth.googleErr", {
            msg: err instanceof Error ? err.message : String(err),
          }),
        );
      } finally {
        setLoading(false);
      }
    },
    [nav, onLogin, t],
  );

  return (
    <div className="auth-page">
      <div style={{ width: "100%", maxWidth: 380 }}>
        <div className="auth-card">
          <div className="auth-logo">Pix.</div>
          <div className="auth-tag">PIX STUDIO ONLINE / PORTAL</div>

          <div className="auth-tabs">
            <button
              className={mode === "login" ? "active" : ""}
              onClick={() => setMode("login")}
            >
              {t("auth.tabLogin")}
            </button>
            <button
              className={mode === "signup" ? "active" : ""}
              onClick={() => setMode("signup")}
            >
              {t("auth.tabSignup")}
            </button>
          </div>

          <form onSubmit={submit}>
            {mode === "signup" && (
              <div className="field">
                <label>{t("auth.lblDisplayName")}</label>
                <input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder={t("auth.phDisplayName")}
                />
              </div>
            )}
            <div className="field">
              <label>{t("auth.lblEmail")}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="field">
              <label>{t("auth.lblPassword")}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            {error && (
              <div
                style={{
                  color: "var(--warn)",
                  fontSize: 11,
                  fontFamily: "var(--font-mono)",
                  marginBottom: 10,
                }}
              >
                {error}
              </div>
            )}

            {mode === "login" && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 11,
                  fontFamily: "var(--font-mono)",
                  marginBottom: 14,
                }}
              >
                <label
                  style={{
                    color: "var(--sub)",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <input type="checkbox" defaultChecked /> {t("auth.remember")}
                </label>
                <a href="#" style={{ color: "var(--primary)" }}>
                  {t("auth.forgot")}
                </a>
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary btn-block btn-lg"
              disabled={loading}
            >
              {mode === "login" ? t("auth.btnLogin") : t("auth.btnSignup")}
            </button>

            <div className="auth-or">
              <span>{t("auth.orDivider")}</span>
            </div>

            <GoogleSignInButton
              onCredential={(c) => void onGoogleCredential(c)}
              onError={(m) => setError(m)}
              text={mode === "signup" ? "signup_with" : "signin_with"}
            />

            <button
              type="button"
              className="btn btn-block btn-sm"
              style={{ marginTop: 10 }}
              onClick={() => void asDemo()}
              disabled={loading}
            >
              {t("auth.demoLogin")}
            </button>

            {mode === "signup" && (
              <div
                style={{
                  fontSize: 11,
                  color: "var(--muted-2)",
                  fontFamily: "var(--font-mono)",
                  lineHeight: 1.7,
                  marginTop: 12,
                }}
              >
                {t("auth.terms", {
                  terms: t("auth.termsLink"),
                  privacy: t("auth.privacyLink"),
                })}
              </div>
            )}
          </form>

          <div className="auth-foot">
            <span>{t("auth.foot")}</span>
          </div>
        </div>

        <div className="auth-cross-footer">
          <a href="https://studio.pixadv.net">{t("auth.backStudio")}</a>
          ·
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              nav({ page: "home" });
            }}
          >
            {t("auth.toPortal")}
          </a>
        </div>
      </div>
    </div>
  );
}
